/**
 * Mock 数据生成工具 - 分析数据生成
 *
 * 职责范围：
 * - 生成分析相关的聚合数据（AggregatedData, TimeSeriesData, MosaicData 等）
 * - 导出 MOCK_FEEDBACK_DATA 常量供全局使用
 * - 用于分析工作台、图表展示等高级分析场景
 *
 * 注意：如需生成基础的反馈数据和仪表板数据，请使用 services/mockData.ts
 */

import {
  FeedbackData,
  SentimentType,
  ExperienceType,
  AggregatedDataItem,
  TimeSeriesDataItem,
  MosaicDataItem,
  WordCloudDataItem,
  ScatterDataItem,
} from '../types/analysis';

// 产品数据
const PRODUCT_CATEGORIES = ['智能手机', '智能手表', '无线耳机', '平板电脑', '笔记本电脑'];
const PRODUCT_BRANDS = ['品牌A', '品牌B', '品牌C', '品牌D', '品牌E'];
const PRODUCT_MODELS: Record<string, string[]> = {
  '智能手机': ['型号X1', '型号X2', '型号X3'],
  '智能手表': ['型号W1', '型号W2'],
  '无线耳机': ['型号E1', '型号E2', '型号E3'],
  '平板电脑': ['型号T1', '型号T2'],
  '笔记本电脑': ['型号L1', '型号L2', '型号L3'],
};

// 渠道数据
const CHANNELS = ['Amazon', 'eBay', 'Facebook', 'Twitter', '客诉系统', '调研系统'];

// 体验类型
const EXPERIENCE_TYPES: ExperienceType[] = [
  'usability',
  'performance',
  'appearance',
  'functionality',
  'reliability',
  'support',
];

// 关键词库
const POSITIVE_KEYWORDS = ['优秀', '好用', '满意', '推荐', '棒', '完美', '流畅', '快', '漂亮', '值得'];
const NEGATIVE_KEYWORDS = ['差', '失望', '问题', '卡顿', '慢', '难用', '不满', '退货', '糟糕', '遗憾'];
const NEUTRAL_KEYWORDS = ['一般', '还行', '可以', '普通', '正常', '尚可', '凑合'];

/**
 * 生成随机整数
 */
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * 随机选择数组元素
 */
function randomChoice<T>(array: T[]): T {
  return array[randomInt(0, array.length - 1)];
}

/**
 * 根据满意度评分生成情感类型
 */
function getSentiment(score: number): SentimentType {
  if (score < 5) return 'negative';
  if (score > 7) return 'positive';
  return 'neutral';
}

/**
 * 根据情感类型生成关键词
 */
function generateKeywords(sentiment: SentimentType): string[] {
  const keywordPool =
    sentiment === 'positive'
      ? POSITIVE_KEYWORDS
      : sentiment === 'negative'
      ? NEGATIVE_KEYWORDS
      : NEUTRAL_KEYWORDS;

  const count = randomInt(2, 4);
  const keywords: string[] = [];
  for (let i = 0; i < count; i++) {
    keywords.push(randomChoice(keywordPool));
  }
  return keywords;
}

/**
 * 生成随机日期（最近30天内）
 */
function randomDate(): string {
  const now = new Date();
  const daysAgo = randomInt(0, 30);
  const date = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
  return date.toISOString();
}

/**
 * 生成模拟反馈数据
 */
export function generateMockFeedback(count: number = 1000): FeedbackData[] {
  const feedbackList: FeedbackData[] = [];

  for (let i = 0; i < count; i++) {
    const category = randomChoice(PRODUCT_CATEGORIES);
    const brand = randomChoice(PRODUCT_BRANDS);
    const models = PRODUCT_MODELS[category];
    const model = models ? randomChoice(models) : '未知型号';
    const channel = randomChoice(CHANNELS);
    const satisfactionScore = randomInt(1, 10);
    const sentiment = getSentiment(satisfactionScore);
    const experienceType = Math.random() > 0.3 ? randomChoice(EXPERIENCE_TYPES) : undefined;
    const keywords = generateKeywords(sentiment);

    const content = `这是一条${sentiment === 'positive' ? '正面' : sentiment === 'negative' ? '负面' : '中性'}的反馈。${keywords.join('，')}。`;

    feedbackList.push({
      id: `feedback-${i + 1}`,
      content,
      productCategory: category,
      productBrand: brand,
      productModel: model,
      channel,
      sentiment,
      satisfactionScore,
      experienceType,
      createdAt: randomDate(),
      keywords,
    });
  }

  return feedbackList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 * 生成聚合数据（柱状图/饼图）
 */
export function generateAggregatedData(feedbackList: FeedbackData[], dimension: string): AggregatedDataItem[] {
  const aggregated: Record<string, number> = {};

  feedbackList.forEach((feedback) => {
    const key = (feedback as any)[dimension] || '其他';
    aggregated[key] = (aggregated[key] || 0) + 1;
  });

  const total = Object.values(aggregated).reduce((sum, val) => sum + val, 0);

  return Object.entries(aggregated)
    .map(([label, value]) => ({
      label,
      value,
      percentage: parseFloat(((value / total) * 100).toFixed(2)),
    }))
    .sort((a, b) => b.value - a.value);
}

/**
 * 生成时间序列数据（折线图）
 */
export function generateTimeSeriesData(feedbackList: FeedbackData[]): TimeSeriesDataItem[] {
  const daily: Record<string, number> = {};

  feedbackList.forEach((feedback) => {
    const date = feedback.createdAt.split('T')[0];
    daily[date] = (daily[date] || 0) + 1;
  });

  return Object.entries(daily)
    .map(([date, value]) => ({ date, value }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * 生成马赛克图数据
 */
export function generateMosaicData(feedbackList: FeedbackData[]): MosaicDataItem[] {
  const mosaicData: MosaicDataItem[] = [];

  // 按体验类型和产品品类分组
  const grouped: Record<string, Record<string, FeedbackData[]>> = {};

  feedbackList.forEach((feedback) => {
    if (!feedback.experienceType) return;

    const expType = feedback.experienceType;
    const product = feedback.productCategory;

    if (!grouped[expType]) grouped[expType] = {};
    if (!grouped[expType][product]) grouped[expType][product] = [];

    grouped[expType][product].push(feedback);
  });

  // 计算每个组的满意度和数量
  Object.entries(grouped).forEach(([expType, products]) => {
    Object.entries(products).forEach(([product, feedbacks]) => {
      const avgSatisfaction = feedbacks.reduce((sum, f) => sum + f.satisfactionScore, 0) / feedbacks.length;
      const sentiment = getSentiment(avgSatisfaction);

      mosaicData.push({
        category: expType as ExperienceType,
        product,
        satisfaction: parseFloat(avgSatisfaction.toFixed(1)),
        count: feedbacks.length,
        sentiment,
      });
    });
  });

  return mosaicData;
}

/**
 * 生成词云数据
 */
export function generateWordCloudData(feedbackList: FeedbackData[]): WordCloudDataItem[] {
  const wordCount: Record<string, { count: number; sentiments: SentimentType[] }> = {};

  feedbackList.forEach((feedback) => {
    feedback.keywords.forEach((keyword) => {
      if (!wordCount[keyword]) {
        wordCount[keyword] = { count: 0, sentiments: [] };
      }
      wordCount[keyword].count += 1;
      wordCount[keyword].sentiments.push(feedback.sentiment);
    });
  });

  return Object.entries(wordCount)
    .map(([word, { count, sentiments }]) => {
      // 统计主要情感
      const sentimentCounts = {
        positive: sentiments.filter((s) => s === 'positive').length,
        negative: sentiments.filter((s) => s === 'negative').length,
        neutral: sentiments.filter((s) => s === 'neutral').length,
      };

      const dominantSentiment =
        sentimentCounts.positive > sentimentCounts.negative && sentimentCounts.positive > sentimentCounts.neutral
          ? 'positive'
          : sentimentCounts.negative > sentimentCounts.neutral
          ? 'negative'
          : 'neutral';

      return {
        word,
        count,
        sentiment: dominantSentiment as SentimentType,
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 50); // 取前50个高频词
}

/**
 * 生成散点图数据（满意度 vs 提及次数）
 */
export function generateScatterData(feedbackList: FeedbackData[]): ScatterDataItem[] {
  // 按产品型号分组
  const grouped: Record<string, FeedbackData[]> = {};

  feedbackList.forEach((feedback) => {
    const key = feedback.productModel;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(feedback);
  });

  return Object.entries(grouped).map(([model, feedbacks]) => {
    const avgSatisfaction = feedbacks.reduce((sum, f) => sum + f.satisfactionScore, 0) / feedbacks.length;
    const count = feedbacks.length;

    return {
      x: avgSatisfaction,
      y: count,
      label: model,
    };
  });
}

// 导出单例 Mock 数据
export const MOCK_FEEDBACK_DATA = generateMockFeedback(1000);
